import {
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type ComponentProps,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import {
  BoldIcon,
  CodeIcon,
  EllipsisIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  TextQuoteIcon,
  type LucideIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { isMacPlatform, modKey } from '@/shared/utils/modKey';
import type { MarkdownFilePaths } from '../Markdown';
import { MarkdownLazy } from '../MarkdownLazy';
import { LinkForm } from './LinkForm';
import {
  insertLink,
  pastedAddress,
  toggleInline,
  toggleLinePrefix,
  type TextEdit,
} from './markdownCommands';

type TextareaProps = Omit<
  ComponentProps<'textarea'>,
  'value' | 'defaultValue' | 'onChange'
>;

/** What the editor hands its text box: the caller's textarea props, plus its own. */
export interface MarkdownInputProps extends TextareaProps {
  value: string;
  onValueChange: (value: string) => void;
}

export interface MarkdownEditorProps extends TextareaProps {
  value: string;
  onValueChange: (value: string) => void;
  /**
   * What Preview renders, when what is saved is not what is typed. The comment
   * composer's text says `@Sam`, and what it sends is the mention link; the
   * preview shows the latter, because it shows what will be posted.
   */
  preview?: string;
  /** The prose class of the place the saved text is read, so the preview matches it. */
  previewClassName?: string;
  /** As the read view passes them, so a path links in the preview as it will once saved. */
  filePaths?: MarkdownFilePaths;
  /**
   * The text box, when the plain `Textarea` is not it. The comment composer
   * supplies `MentionTextarea` here, which keeps its `@` picker. Whatever is
   * returned must render a `<textarea>` and pass these props to it.
   */
  renderInput?: (props: MarkdownInputProps) => ReactElement;
  /**
   * The text box, for whoever needs to focus it: react-hook-form's `field.ref`,
   * so a failed submit focuses the description. Attached to the plain
   * `Textarea`; with `renderInput` it is handed on in its props, as before.
   */
  ref?: Ref<HTMLTextAreaElement>;
}

type ActionId =
  'bold' | 'italic' | 'link' | 'bullet' | 'numbered' | 'code' | 'quote';

interface Action {
  id: ActionId;
  label: string;
  icon: LucideIcon;
  /** The letter that, with Ctrl/⌘, does this while the text has focus. */
  key?: string;
  /** Moved into the More menu when the editor is narrow. */
  rare?: boolean;
  edit?: (edit: TextEdit) => TextEdit;
}

const ACTIONS: Action[] = [
  {
    id: 'bold',
    label: 'Bold',
    icon: BoldIcon,
    key: 'b',
    edit: (e) => toggleInline(e, '**'),
  },
  {
    id: 'italic',
    label: 'Italic',
    icon: ItalicIcon,
    key: 'i',
    edit: (e) => toggleInline(e, '_'),
  },
  { id: 'link', label: 'Link', icon: LinkIcon, key: 'k' },
  {
    id: 'bullet',
    label: 'Bulleted list',
    icon: ListIcon,
    edit: (e) => toggleLinePrefix(e, 'bullet'),
  },
  {
    id: 'numbered',
    label: 'Numbered list',
    icon: ListOrderedIcon,
    edit: (e) => toggleLinePrefix(e, 'numbered'),
  },
  {
    id: 'code',
    label: 'Code',
    icon: CodeIcon,
    rare: true,
    edit: (e) => toggleInline(e, '`'),
  },
  {
    id: 'quote',
    label: 'Quote',
    icon: TextQuoteIcon,
    rare: true,
    edit: (e) => toggleLinePrefix(e, 'quote'),
  },
];

const MOD = modKey();

/**
 * Markdown, written (spec 145 FR-001): a text box with a toolbar, a link form
 * and Write / Preview tabs, used by every place a task's description or a
 * comment is written, so all three offer the same buttons, keys and preview.
 *
 * **Still a text box underneath.** The browser's spell check, autocomplete and
 * undo, and the composer's `@` picker, keep working because nothing replaces
 * the `<textarea>`; the toolbar only edits its text.
 *
 * **Each command is one undo step.** A command is a pure edit
 * (`markdownCommands`), applied with `execCommand('insertText')`: deprecated,
 * and still the only way to change a textarea's text that the browser's own
 * undo history records. Where it is missing or refuses, the value is set
 * directly and an `input` event sent, so the owner's `onChange` hears it as
 * typing; that path loses the undo step, and nothing else.
 *
 * **Preview hides the text box rather than unmounting it**, so the text, the
 * caret and the undo history are where they were on the way back (FR-005). It
 * renders through `MarkdownLazy`, the same renderer, prose class and file
 * links as the place the text will be read.
 *
 * **Keys are the editor's while it has focus.** Ctrl/⌘+B, I and K are taken on
 * the text box and stopped there: ⌘K anywhere else still opens the command
 * palette, which listens on `window` and so never hears these.
 *
 * **Narrow, it folds rather than scrolls.** The editor is a container, and
 * below about 360px Code and Quote move into a More menu; the row also wraps,
 * so it never pushes the page sideways.
 */
export function MarkdownEditor({
  value,
  onValueChange,
  preview,
  previewClassName,
  filePaths,
  renderInput,
  onKeyDown,
  onPaste,
  ref,
  ...textareaProps
}: MarkdownEditorProps) {
  const baseId = useId();
  const writeTabId = `${baseId}-write-tab`;
  const previewTabId = `${baseId}-preview-tab`;
  const writePanelId = `${baseId}-write`;
  const previewPanelId = `${baseId}-preview`;
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const area = useRef<HTMLDivElement>(null);
  // The link form: the selection it will replace, and its words. A new `key`
  // each time it opens, so it starts from the selection rather than from
  // whatever the last one held.
  const [link, setLink] = useState<{
    start: number;
    end: number;
    text: string;
    key: number;
  } | null>(null);
  const disabled = textareaProps.disabled === true;

  /** The text box, found rather than ref'd: `renderInput` may own its ref. */
  const textarea = (): HTMLTextAreaElement | null =>
    area.current?.querySelector('textarea') ?? null;

  const apply = (make: (edit: TextEdit) => TextEdit) => {
    const box = textarea();
    if (!box || box.disabled || box.readOnly) return;
    const next = make({
      text: box.value,
      selectionStart: box.selectionStart,
      selectionEnd: box.selectionEnd,
    });
    replaceText(box, next.text);
    box.setSelectionRange(next.selectionStart, next.selectionEnd);
  };

  const openLink = () => {
    const box = textarea();
    if (!box || box.disabled) return;
    const { selectionStart: start, selectionEnd: end } = box;
    setLink((previous) => ({
      start,
      end,
      text: box.value.slice(start, end),
      key: (previous?.key ?? 0) + 1,
    }));
  };

  const run = (action: Action) => {
    if (action.id === 'link') {
      openLink();
      return;
    }
    if (action.edit) apply(action.edit);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const mod = isMacPlatform() ? event.metaKey : event.ctrlKey;
    const action =
      mod && !event.altKey && !event.shiftKey
        ? ACTIONS.find((a) => a.key === event.key.toLowerCase())
        : undefined;
    if (action) {
      event.preventDefault();
      // Stopped so the palette's ⌘K and any other app key do not also act.
      event.stopPropagation();
      run(action);
      return;
    }
    onKeyDown?.(event);
  };

  // A web address pasted over selected words links them (FR-004). Anything
  // else, or a paste with nothing selected, is an ordinary paste.
  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    onPaste?.(event);
    if (event.defaultPrevented) return;
    const box = event.currentTarget;
    const { selectionStart: start, selectionEnd: end } = box;
    if (start === end) return;
    const url = pastedAddress(event.clipboardData.getData('text/plain'));
    if (url === null) return;
    event.preventDefault();
    apply((edit) => insertLink(edit, box.value.slice(start, end), url));
  };

  const inputProps: MarkdownInputProps = {
    ...textareaProps,
    ref,
    value,
    onValueChange,
    onKeyDown: handleKeyDown,
    onPaste: handlePaste,
  };
  const shown = preview ?? value;

  return (
    <div className="@container flex flex-col gap-1.5">
      <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
        <TabsList aria-label="Write or preview">
          <TabsTrigger
            id={writeTabId}
            selected={mode === 'write'}
            panelId={writePanelId}
            onClick={() => {
              setMode('write');
            }}
          >
            Write
          </TabsTrigger>
          <TabsTrigger
            id={previewTabId}
            selected={mode === 'preview'}
            panelId={previewPanelId}
            onClick={() => {
              setMode('preview');
            }}
          >
            Preview
          </TabsTrigger>
        </TabsList>
        {mode === 'write' ? (
          <Toolbar
            disabled={disabled}
            onAction={run}
            linkOpen={link !== null}
            onLinkOpenChange={(open) => {
              if (open) openLink();
              else setLink(null);
            }}
            linkForm={
              link ? (
                <LinkForm
                  key={link.key}
                  initialText={link.text}
                  returnFocusTo={textarea}
                  onCancel={() => {
                    setLink(null);
                  }}
                  onAdd={(text, url) => {
                    // The selection as it was when the form opened: focus has
                    // been in the form since, so nothing could have moved it.
                    apply((edit) =>
                      insertLink(
                        {
                          ...edit,
                          selectionStart: link.start,
                          selectionEnd: link.end,
                        },
                        text,
                        url,
                      ),
                    );
                    setLink(null);
                  }}
                />
              ) : null
            }
            returnFocusTo={textarea}
          />
        ) : null}
      </div>

      <div
        id={writePanelId}
        role="tabpanel"
        aria-labelledby={writeTabId}
        hidden={mode !== 'write'}
        ref={area}
      >
        {renderInput ? (
          // The handlers in `inputProps` read the text box's ref only when an
          // event calls them, never while rendering.
          // eslint-disable-next-line react-hooks/refs -- see above
          renderInput(inputProps)
        ) : (
          <Textarea
            {...textareaProps}
            ref={ref}
            value={value}
            onChange={(event) => {
              onValueChange(event.target.value);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
        )}
      </div>

      <div
        id={previewPanelId}
        role="tabpanel"
        aria-labelledby={previewTabId}
        hidden={mode !== 'preview'}
        // The text box's frame, so switching does not make the page jump.
        className="border-input min-h-16 rounded-lg border px-2.5 py-2"
      >
        {mode === 'preview' ? (
          shown.trim() === '' ? (
            <p className="text-muted-foreground text-sm">Nothing to preview</p>
          ) : (
            <MarkdownLazy
              content={shown}
              className={previewClassName}
              filePaths={filePaths}
            />
          )
        ) : null}
      </div>
    </div>
  );
}

/**
 * Replace the text box's text with `next`, as one edit the browser can undo
 * where it allows that.
 *
 * Only the part that changed is replaced, so a long description is not
 * re-inserted whole for one `**`, and the box does not scroll to its end.
 */
function replaceText(box: HTMLTextAreaElement, next: string) {
  const previous = box.value;
  if (previous === next) return;
  let head = 0;
  while (
    head < previous.length &&
    head < next.length &&
    previous[head] === next[head]
  ) {
    head += 1;
  }
  let tail = 0;
  while (
    tail < previous.length - head &&
    tail < next.length - head &&
    previous[previous.length - 1 - tail] === next[next.length - 1 - tail]
  ) {
    tail += 1;
  }
  const inserted = next.slice(head, next.length - tail);

  box.focus();
  box.setSelectionRange(head, previous.length - tail);
  let done = false;
  try {
    // Asked first because jsdom, and so every test, has no `execCommand` at
    // all. An empty insertion is a deletion, which `insertText` may decline.
    done =
      'execCommand' in document &&
      document.execCommand(
        inserted === '' ? 'delete' : 'insertText',
        false,
        inserted,
      );
  } catch {
    done = false;
  }
  if (done && box.value === next) return;
  // The fallback: set the value the way React does not track, so the `input`
  // event below reaches the owner's `onChange` as a change.
  Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    'value',
  )?.set?.call(box, next);
  box.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * The seven buttons, as one labelled toolbar with one tab stop: Tab reaches
 * it once, and the arrow keys move along it (the toolbar pattern). Every
 * button keeps the text's selection by not taking focus on press, so Bold
 * acts on what was selected rather than on nothing.
 */
function Toolbar({
  disabled,
  onAction,
  linkOpen,
  onLinkOpenChange,
  linkForm,
  returnFocusTo,
}: {
  disabled: boolean;
  onAction: (action: Action) => void;
  linkOpen: boolean;
  onLinkOpenChange: (open: boolean) => void;
  linkForm: ReactNode;
  returnFocusTo: () => HTMLElement | null;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(0);

  // The buttons the reader can see: the rare ones are hidden when narrow, and
  // the More button when not.
  const visibleButtons = () =>
    Array.from(
      bar.current?.querySelectorAll<HTMLButtonElement>(
        'button[data-toolbar-item]',
      ) ?? [],
    ).filter((button) => getComputedStyle(button).display !== 'none');

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const buttons = visibleButtons();
    if (buttons.length === 0) return;
    const from = buttons.findIndex((b) => b === document.activeElement);
    let next: number;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = buttons.length - 1;
    else {
      const step = event.key === 'ArrowRight' ? 1 : -1;
      next = (from + step + buttons.length) % buttons.length;
    }
    event.preventDefault();
    buttons[next]?.focus();
  };

  const tabIndexOf = (index: number) => (index === current ? 0 : -1);
  const itemProps = (index: number) => ({
    'data-toolbar-item': '',
    tabIndex: tabIndexOf(index),
    onFocus: () => {
      setCurrent(index);
    },
  });

  return (
    <div
      ref={bar}
      role="toolbar"
      aria-label="Formatting"
      onKeyDown={onKeyDown}
      className="-mb-0.5 flex flex-wrap items-center gap-0.5"
    >
      {ACTIONS.map((action, index) => {
        const Icon = action.icon;
        const hint = action.key
          ? `${action.label} (${MOD}+${action.key.toUpperCase()})`
          : action.label;
        const button = (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={action.label}
            aria-keyshortcuts={
              action.key
                ? `${isMacPlatform() ? 'Meta' : 'Control'}+${action.key.toUpperCase()}`
                : undefined
            }
            disabled={disabled}
            className={cn(
              'text-muted-foreground hover:text-foreground',
              action.rare ? '@max-[22.5rem]:hidden' : undefined,
            )}
            // Pressing a button must not take the text's selection with it.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={
              action.id === 'link'
                ? undefined
                : () => {
                    onAction(action);
                  }
            }
            {...itemProps(index)}
          >
            <Icon aria-hidden="true" />
          </Button>
        );
        if (action.id === 'link') {
          return (
            <Popover
              key={action.id}
              open={linkOpen}
              onOpenChange={onLinkOpenChange}
            >
              <Tooltip>
                <TooltipTrigger render={<PopoverTrigger render={button} />} />
                <TooltipContent>{hint}</TooltipContent>
              </Tooltip>
              {linkForm}
            </Popover>
          );
        }
        return (
          <Tooltip key={action.id}>
            <TooltipTrigger render={button} />
            <TooltipContent>{hint}</TooltipContent>
          </Tooltip>
        );
      })}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="More formatting"
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground hidden @max-[22.5rem]:inline-flex"
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              {...itemProps(ACTIONS.length)}
            />
          }
        >
          <EllipsisIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" finalFocus={returnFocusTo}>
          {ACTIONS.filter((action) => action.rare).map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.id}
                onClick={() => {
                  onAction(action);
                }}
              >
                <Icon aria-hidden="true" />
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
